import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReasonPayload } from "@/services/reasonCodes";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useSearchParams } from "react-router-dom";
import { Pencil, Check, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPriceBookMatrix, saveChannelOverrides, type ChannelOverrideInput, type PriceBookMatrixProduct } from "@/services/pricing";
import { PageShell, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { useCan } from "@/auth/permissions";
import { PricingSubnav } from "./PricingSubnav";

/**
 * 售价管理中心 · 同行价目表（/admin/pricing/price-book · PRICEBOOK-1）。
 * 单一真源 = 成本引擎：全部产品 × 1–4 人一房，每格两个数——同行结算价 + 同行建议售价，标来源（固定 = 本产品设了固定金额，自动 = 系数算出）。
 * 调价 = 对每个改动的产品调 PUT /pricing/products/{id}/channel-overrides（同一套校验 / 原因 / 低于成本 / 清缓存 / 重推 / 改价记录）；
 * 留空 = 这一档回落系数（自动）。不住宿的产品不分房，只有一格。旧价目表（b2b_price_book）不再读写。
 */

const OCCS = [1, 2, 3, 4] as const;
const OCC_LABEL: Record<number, string> = { 1: "单人房", 2: "2 人一间", 3: "3 人一间", 4: "4 人一间" };
const usd2 = (c?: number | null) => (c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dollars = (c: number | null | undefined) => (c == null ? "" : String(+(c / 100).toFixed(2)));
const cents = (t: string) => Math.round(Number(t.trim()) * 100);
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const INP = "h-7 w-[84px] rounded-md border px-1.5 text-right text-[12.5px] tabular-nums focus:border-blue-500 focus:outline-none";

type CellDraft = { net: string; list: string };
type Draft = Record<string, Record<number, CellDraft>>;

/** 来源小标：固定 = 本产品设了固定金额；自动 = 系数算出 */
function Src({ s }: { s: "fixed" | "auto" }) {
  return <span className={`ml-1 inline-flex h-4 items-center rounded px-1 text-[10px] font-semibold ${s === "fixed" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500"}`} data-testid={`src-${s}`}>{s === "fixed" ? "固定" : "自动"}</span>;
}

function moneyError(t: string): string | null {
  const s = t.trim();
  if (!s) return null;
  return DECIMAL2.test(s) ? null : "金额最多两位小数";
}

/** 某产品的初始草稿：只有「固定」的格子才预填（自动的格子留空，placeholder 显示自动值） */
function initFor(p: PriceBookMatrixProduct): Record<number, CellDraft> {
  const out: Record<number, CellDraft> = {};
  for (const o of p.occupancies ?? []) {
    const c = p.cells?.[String(o)];
    out[o] = { net: c?.net_source === "fixed" ? dollars(c.net_cents) : "", list: c?.list_source === "fixed" ? dollars(c.list_cents) : "" };
  }
  return out;
}

/** 这一格的校验：金额格式；建议售价不能低于结算价（结算价取填的值，没填取自动值） */
function cellError(p: PriceBookMatrixProduct, o: number, d: CellDraft): string | null {
  const e = moneyError(d.net) ?? moneyError(d.list);
  if (e) return e;
  if (d.list.trim()) {
    const net = d.net.trim() ? cents(d.net) : p.cells?.[String(o)]?.net_cents;
    if (net != null && cents(d.list) < net) return `建议售价不能低于结算价 ${usd2(net)}`;
  }
  return null;
}

/** 草稿 → 这个产品的 b2b 渠道入参；每格都留空 = 恢复系数。只填建议售价没填任何结算价 = 报错（建议售价分档必须搭配结算价）。 */
function toInput(p: PriceBookMatrixProduct, d: Record<number, CellDraft>): ChannelOverrideInput | string {
  const ov = p.override!;
  const base = { channel: "b2b", markup_pct: ov.markup_pct, commission_pct: ov.commission_pct };
  const occs = p.occupancies ?? [];
  const nets = occs.filter((o) => d[o]?.net.trim());
  const lists = occs.filter((o) => d[o]?.list.trim());
  if (!nets.length && lists.length) return `${p.code}：建议售价要搭配至少一个结算价（固定金额）一起填`;
  if (!nets.length) return { ...base, mode: "markup" };
  if (!p.multi) {
    const o = occs[0];
    return { ...base, mode: "fixed", fixed_price_cents: cents(d[o].net), list_price_override_cents: d[o].list.trim() ? cents(d[o].list) : null };
  }
  return {
    ...base, mode: "fixed", fixed_price_cents: null,
    fixed_tier_prices: Object.fromEntries(nets.map((o) => [String(o), cents(d[o].net)])),
    list_tier_prices: Object.fromEntries(lists.map((o) => [String(o), cents(d[o].list)])),
  };
}

export function PriceBookPage() {
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const focusId = sp.get("focus") || "";   // 从产品售价详情「调价」带入·定位该产品
  const canEditPerm = useCan("pricing.channel_config.update");   // 写入走 channel-overrides，权限点与「调整渠道价格」一致

  const [editing, setEditing] = useState(false);
  const [scopedPid, setScopedPid] = useState("");   // "" = 全部产品；非空 = 仅该产品
  const [draft, setDraft] = useState<Draft>({});
  const [failures, setFailures] = useState<string[]>([]);
  const focusRef = useRef<HTMLTableRowElement | null>(null);
  const didAuto = useRef(false);

  const matrixQ = useQuery({ queryKey: ["pricing", "price-book-matrix"], queryFn: getPriceBookMatrix });
  const products = matrixQ.data?.products ?? [];
  const canEdit = canEditPerm && !!matrixQ.data?.editable;
  const readOnlyNote = canEditPerm && matrixQ.data && !matrixQ.data.editable;

  const enterEdit = (scope = "") => {
    setScopedPid(scope);
    setFailures([]);
    setDraft(Object.fromEntries(products.filter((p) => p.cells).map((p) => [p.product_id, initFor(p)])));
    setEditing(true);
  };

  // 从产品售价详情「调价」进入：定位该产品 + 滚动 + 自动进调价模式（一次）
  useEffect(() => {
    if (!focusId || !products.length || didAuto.current) return;
    if (!products.some((p) => p.product_id === focusId)) return;
    didAuto.current = true;
    if (canEdit) enterEdit(focusId);
    requestAnimationFrame(() => focusRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, products, canEdit]);

  const targets = useMemo(() => products.filter((p) => p.cells && (!scopedPid || p.product_id === scopedPid)), [products, scopedPid]);
  const changedOf = (p: PriceBookMatrixProduct) => {
    const init = initFor(p);
    return (p.occupancies ?? []).filter((o) => draft[p.product_id]?.[o] && (draft[p.product_id][o].net !== init[o].net || draft[p.product_id][o].list !== init[o].list));
  };
  const errCount = editing ? targets.reduce((n, p) => n + (p.occupancies ?? []).filter((o) => draft[p.product_id]?.[o] && cellError(p, o, draft[p.product_id][o])).length, 0) : 0;
  const changedProducts = editing ? targets.filter((p) => changedOf(p).length > 0) : [];

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const saveMut = useMutation({
    mutationFn: async () => {
      const changes = changedProducts.flatMap((p) => changedOf(p).flatMap((o) => {
        const c = p.cells![String(o)];
        const d = draft[p.product_id][o];
        const row = (label: string, before: number, srcB: string, typed: string) => ({ label: `${p.code} · ${OCC_LABEL[o]} ${label}`, before: `${usd2(before)}（${srcB}）`, after: typed.trim() ? usd2(cents(typed)) : "回落系数（自动）" });
        const init = initFor(p)[o];
        return [
          ...(d.net !== init.net ? [row("结算价", c.net_cents, c.net_source === "fixed" ? "固定" : "自动", d.net)] : []),
          ...(d.list !== init.list ? [row("建议售价", c.list_cents, c.list_source === "fixed" ? "固定" : "自动", d.list)] : []),
        ];
      }));
      const run = async (list: PriceBookMatrixProduct[], reason: ReasonPayload) => {
        const bad: { p: PriceBookMatrixProduct; msg: string }[] = [];
        for (const p of list) {   // 逐个产品顺序调用（每个产品一次完整的保存：校验 / 原因 / 清缓存 / 重推 / 改价记录）
          const inp = toInput(p, draft[p.product_id]);
          if (typeof inp === "string") { bad.push({ p, msg: inp }); continue; }
          try { await saveChannelOverrides(p.product_id, [inp], reason); } catch (e) { bad.push({ p, msg: (e as Error).message || "保存失败" }); }
        }
        return bad;
      };
      let r: ReasonPayload = {};
      const got = await askReason({
        scene: "price_change", title: "调整同行价目表", subtitle: `共 ${changedProducts.length} 个产品有变化`,
        changes: changes.slice(0, 8).concat(changes.length > 8 ? [{ label: `…等共 ${changes.length} 项`, before: "", after: "" }] : []), confirmText: "确认并保存",
      });
      if (!got) throw new ReasonCancelled();
      r = got;
      let bad = await run(changedProducts, r);
      const below = bad.filter((b) => /低于成本/.test(b.msg));
      if (below.length) {   // 后端判出低于成本：补充说明必填，再问一次，只重试这些产品
        const again = await askReason({
          scene: "price_change", title: "价格低于成本", subtitle: below.map((b) => b.p.code).join("、"), changes: [],
          confirmText: "确认并保存", textRequired: true, textRequiredHint: "价格低于成本，必须补充说明",
        });
        if (again) bad = bad.filter((b) => !below.includes(b)).concat(await run(below.map((b) => b.p), again));
      }
      return bad.map((b) => `${b.p.code}：${b.msg}`);
    },
    onSuccess: (bad) => {
      qc.invalidateQueries({ queryKey: ["pricing"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      setFailures(bad);
      if (!bad.length) { setEditing(false); setScopedPid(""); }
    },
    onError: (e) => { if (!isReasonCancelled(e)) setFailures([(e as Error).message || "调价保存失败"]); },
  });

  const setCell = (pid: string, o: number, part: Partial<CellDraft>) => setDraft((d) => ({ ...d, [pid]: { ...d[pid], [o]: { ...d[pid][o], ...part } } }));

  const renderCell = (p: PriceBookMatrixProduct, o: number, caption?: string) => {
    const c = p.cells?.[String(o)];
    if (!c) return <span className="text-slate-400">—</span>;
    const d = draft[p.product_id]?.[o];
    const inEdit = editing && (!scopedPid || p.product_id === scopedPid) && !!d;
    if (!inEdit) {
      return (
        <div className="flex flex-col gap-0.5" data-testid={`cell-${p.code}-${o}`}>
          {caption && <span className="text-[11px] text-slate-400">{caption}</span>}
          <span className="whitespace-nowrap text-slate-700"><span className="mr-1 text-[11px] text-slate-400">结算</span><b className="tabular-nums text-blue-700">{usd2(c.net_cents)}</b><Src s={c.net_source} /></span>
          <span className="whitespace-nowrap text-slate-700"><span className="mr-1 text-[11px] text-slate-400">建议</span><b className="tabular-nums text-slate-900">{usd2(c.list_cents)}</b><Src s={c.list_source} /></span>
        </div>
      );
    }
    const err = cellError(p, o, d);
    return (
      <div className="flex flex-col gap-1" data-testid={`cell-${p.code}-${o}`}>
        {caption && <span className="text-[11px] text-slate-400">{caption}</span>}
        <label className="flex items-center gap-1 text-[11px] text-slate-400">结算 $<input aria-label={`${p.code} ${OCC_LABEL[o]} 结算价`} data-testid={`net-${p.code}-${o}`} className={`${INP} ${moneyError(d.net) ? "border-rose-400" : "border-slate-300"}`} value={d.net} placeholder={dollars(c.net_cents)} onChange={(e) => setCell(p.product_id, o, { net: e.target.value })} /></label>
        <label className="flex items-center gap-1 text-[11px] text-slate-400">建议 $<input aria-label={`${p.code} ${OCC_LABEL[o]} 建议售价`} data-testid={`list-${p.code}-${o}`} className={`${INP} ${moneyError(d.list) || err ? "border-rose-400" : "border-slate-300"}`} value={d.list} placeholder={dollars(c.list_cents)} onChange={(e) => setCell(p.product_id, o, { list: e.target.value })} /></label>
        {err && <span className="max-w-[160px] text-[11px] text-rose-600" data-testid={`err-${p.code}-${o}`}>{err}</span>}
      </div>
    );
  };

  return (
    <PageShell
      title="同行价目表"
      subtitle="数据来自成本引擎（与成本概览、同行站同一口径）· 产品 × 住房人数档 · 同行结算价 + 同行建议售价（门票 & 必付另付）"
      actions={
        <div className="flex gap-2">
          {canEdit && !editing && (
            <button onClick={() => enterEdit("")} className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700"><Pencil className="h-3.5 w-3.5" /> 调价（全部）</button>
          )}
          {canEdit && editing && (
            <>
              <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || errCount > 0 || changedProducts.length === 0} data-testid="pb-save" className="flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> {saveMut.isPending ? "保存中…" : "保存"}</button>
              <button onClick={() => { setEditing(false); setScopedPid(""); setFailures([]); }} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-600 hover:bg-slate-50"><X className="h-3.5 w-3.5" /> 取消</button>
            </>
          )}
        </div>
      }
    >
      <PricingSubnav />
      <div className="text-[12px] text-slate-400">售价管理中心 › 同行价目表</div>

      {editing && (
        <div>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{scopedPid ? `调价模式 · 仅「${products.find((p) => p.product_id === scopedPid)?.name_cn ?? "本产品"}」` : "调价模式 · 全部产品"} · 填了 = 固定金额 · 留空 = 回落系数（自动，灰字是当前自动值）</span>
        </div>
      )}
      {readOnlyNote && <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600" data-testid="pb-readonly">你没有「成本查看」权限，价目表只读（改价会连带系数一起保存，没有权限看系数时不开放）。</div>}
      {failures.length > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="pb-failures">
          {failures.map((f) => <div key={f}>保存失败 · {f}</div>)}
        </div>
      )}

      <Card title="同行价目表 · 全年统一价">
        {matrixQ.isLoading ? (
          <LoadingState />
        ) : matrixQ.isError ? (
          <div className="flex flex-col items-center gap-2 py-8 text-[13px] text-slate-500"><span>加载失败 · 请重试</span><button onClick={() => matrixQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button></div>
        ) : products.length === 0 ? (
          <EmptyState message="暂无产品" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[860px] text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">产品</th>
                  <th className="px-4 py-3 text-left">SKU 代码</th>
                  {OCCS.map((o) => <th key={o} className="px-4 py-3 text-left">{OCC_LABEL[o]}</th>)}
                  {canEdit && <th className="px-4 py-3 text-right">操作</th>}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <Fragment key={p.product_id}>
                  <tr ref={p.product_id === focusId ? focusRef : undefined} className={`border-t border-slate-100 align-top hover:bg-slate-50 ${p.product_id === focusId || (editing && p.product_id === scopedPid) ? "bg-blue-50 ring-1 ring-inset ring-blue-300" : ""} ${editing && scopedPid && p.product_id !== scopedPid ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.name_cn}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-600">{p.code}</td>
                    {p.error ? (
                      <td colSpan={4} className="px-4 py-3 text-[12.5px] text-amber-700">{p.error}</td>
                    ) : p.multi ? (
                      OCCS.map((o) => <td key={o} className="px-4 py-3">{renderCell(p, o)}</td>)
                    ) : (
                      <td colSpan={4} className="px-4 py-3">{renderCell(p, (p.occupancies ?? [2])[0], "每人价（不分房）")}</td>
                    )}
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        {!editing && !p.error && <button onClick={() => enterEdit(p.product_id)} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">调价</button>}
                        {editing && scopedPid === p.product_id && <span className="text-[11px] font-medium text-amber-600">编辑中</span>}
                      </td>
                    )}
                  </tr>
                  {/* OPTPRICE-1 · 每个非默认选项一行（只读）：同价 = 「同产品」；单独定价 = 同行结算 / 建议逐档 + 固定 / 自动 */}
                  {(p.options ?? []).map((o) => (
                    <tr key={o.option_id} data-testid={`option-row-${o.code}`} className="border-t border-dashed border-slate-100 align-top text-slate-600">
                      <td className="px-4 py-2 pl-8 text-[12.5px]">↳ {o.name_cn}</td>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-500">{o.code}</td>
                      {o.same_as_product ? (
                        <td colSpan={4} className="px-4 py-2"><span data-testid={`option-same-${o.code}`} className="rounded-full border border-slate-200 px-2 py-px text-[11px] text-slate-500">同产品</span></td>
                      ) : o.error ? (
                        <td colSpan={4} className="px-4 py-2 text-[12.5px] text-amber-700">{o.error}</td>
                      ) : p.multi ? (
                        OCCS.map((n) => {
                          const c = o.cells?.[String(n)];
                          return <td key={n} className="px-4 py-2">{c ? <div data-testid={`option-cell-${o.code}-${n}`} className="flex flex-col gap-0.5"><span className="whitespace-nowrap"><span className="mr-1 text-[11px] text-slate-400">结算</span><b className="tabular-nums text-blue-700">{usd2(c.net_cents)}</b><Src s={c.net_source} /></span><span className="whitespace-nowrap"><span className="mr-1 text-[11px] text-slate-400">建议</span><b className="tabular-nums text-slate-900">{usd2(c.list_cents)}</b><Src s={c.list_source} /></span></div> : <span className="text-slate-400">—</span>}</td>;
                        })
                      ) : (
                        <td colSpan={4} className="px-4 py-2">{(() => {
                          const c = o.cells?.[String((p.occupancies ?? [2])[0])];
                          return c ? <div data-testid={`option-cell-${o.code}`} className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-400">每人价（不分房）</span><span className="whitespace-nowrap"><span className="mr-1 text-[11px] text-slate-400">结算</span><b className="tabular-nums text-blue-700">{usd2(c.net_cents)}</b><Src s={c.net_source} /></span><span className="whitespace-nowrap"><span className="mr-1 text-[11px] text-slate-400">建议</span><b className="tabular-nums text-slate-900">{usd2(c.list_cents)}</b><Src s={c.list_source} /></span></div> : <span className="text-slate-400">—</span>;
                        })()}</td>
                      )}
                      {canEdit && <td className="px-4 py-2 text-right text-[11px] text-slate-400">在选项里改</td>}
                    </tr>
                  ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 text-[11px] text-slate-400">「固定」= 这个产品的同行渠道设了固定金额；「自动」= 成本 × 系数算出，成本或系数变了会跟着变。建议售价留空 = 结算价 × (1 + 同行建议加价率)。不住宿的产品不分房，只有一个每人价。改价与「调整渠道价格」是同一套保存（原因 / 低于成本说明 / 改价记录都一样）。</div>
      </Card>
      {reasonDialog}
    </PageShell>
  );
}
